import React, { useEffect, useState, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import CustomerDispatchReportViewer from './CustomerDispatchReportViewer';

import CustomerStatsPage from './CustomerStatsPage';
import CustomerReportPage from './CustomerReportPage';
import { AppBar, Toolbar, IconButton, Typography, Stack, Box, TextField, useMediaQuery, Tabs, Tab, Button } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Outlet, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import CustomerSettlementViewer from './CustomerSettlementViewer';
import * as XLSX from 'xlsx';

import {
  CUSTOMER_INFO_MAP,
  resolveCustomerIdFromUserDoc,
  CUSTOMER_COMPANY_MAP as CONFIG_COMPANY_MAP,
  type CustomerId
} from '../config/customerConfig';
import { applyItemNameFilter } from '../utils/customerItemNameFilter';
import { applyRouteFilter } from '../utils/customerRouteFilter';
import { OttogiViewTypeSelector } from './CustomerDispatchReportViewer';
import { CustomerTopNav } from './CustomerTopNav';

// 화주 정보 매핑을 위한 타입 (기존 타입과 호환성 유지)
interface LegacyCustomerInfo {
  name: string;
  logoPath: string;
  color: string;
  backgroundColor: string;
  gradientBackground: string;
  lightColor: string;
}

// config의 CustomerInfo를 기존 타입으로 변환하는 함수
const convertToLegacyFormat = (customerId: CustomerId): LegacyCustomerInfo => {
  const info = CUSTOMER_INFO_MAP[customerId];
  return {
    name: info.name,
    logoPath: `/logos/${customerId === 'otoki_ramen' ? 'otoki' : customerId === 'dole' ? 'dole' : customerId}-logo.png`,
    color: info.color,
    backgroundColor: info.backgroundColor,
    gradientBackground: info.backgroundColor,
    lightColor: info.lightColor
  };
};

// 화주 ID → 실제 Company 컬럼값 매핑 (BigQuery API 호출용)
export const CUSTOMER_COMPANY_MAP: Record<string, string> = CONFIG_COMPANY_MAP;

// 날짜 범위의 모든 날짜 배열 생성 함수
function getDateList(start: string, end: string) {
  const result: string[] = [];
  let cur = new Date(start);
  const endDate = new Date(end);
  while (cur <= endDate) {
    result.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
  }
  
  export const CustomerDispatchPage = ({ 
  onNavigateToMain, 
  initialTabIndex = 0, 
  showStatsTab = false, 
  showStatsInReport = false,
  itemNameFilterType = 'all',
  itemNameList = [],
  routeFilterType = 'all',
  routeList = [],
  currentScreenIndex = 1,
  screenConfig = [],
  hideTopTabs = false
}: { 
  onNavigateToMain?: () => void, 
  initialTabIndex?: number, 
  showStatsTab?: boolean, 
  showStatsInReport?: boolean,
  itemNameFilterType?: 'all' | 'allow' | 'block',
  itemNameList?: string[],
  routeFilterType?: 'all' | 'allow',
  routeList?: string[],
  currentScreenIndex?: number,
  screenConfig?: Array<{id: string, label: string}>,
  hideTopTabs?: boolean
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [user, setUser] = useState<User | null>(null);
  const [customerInfo, setCustomerInfo] = useState<LegacyCustomerInfo | null>(null);
  const [viewType, setViewType] = useState<'individual' | 'summary'>('individual');
  const [customerId, setCustomerId] = useState<string>('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser?.email) {
        const userSnap = await getDoc(doc(db, 'users', currentUser.email));
        const cidField = userSnap.exists() ? userSnap.data()?.customerId : undefined;
        const resolved = resolveCustomerIdFromUserDoc(currentUser.email, cidField);
        setCustomerInfo(convertToLegacyFormat(resolved));
        setCustomerId(resolved);
      }
    });
    return () => unsubscribe();
  }, []);

  // 상태/변수 선언
  const dispatchScrollContainerRef = useRef<HTMLDivElement>(null);
  // PC는 표형, 모바일은 카드형으로 고정
  const viewMode = isMobile ? 'card' : 'table';
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [currentCompany, setCurrentCompany] = useState<string>('');
  const [settlementDateRange, setSettlementDateRange] = useState<{ start: string; end: string }>(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    return { start: todayStr, end: todayStr };
  });
  const [settlementTotalFee, setSettlementTotalFee] = useState(0);
  const [settlementExcelRows, setSettlementExcelRows] = useState<{ headers: string[]; rows: any[][]; company?: string } | null>(null);
  const [settlementHasQueried, setSettlementHasQueried] = useState(false);

  // dateRange: 실제 조회용, pendingDateRange: UI용
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    return { start: todayStr, end: todayStr };
  });
  const [pendingDateRange, setPendingDateRange] = useState(dateRange);

  // 오늘 날짜와 두달 전 날짜 계산
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  // maxDate는 제거(미래 무제한)
  const twoMonthsAgo = new Date(today);
  twoMonthsAgo.setMonth(today.getMonth() - 2);
  const minYyyy = twoMonthsAgo.getFullYear();
  const minMm = String(twoMonthsAgo.getMonth() + 1).padStart(2, '0');
  const minDd = String(twoMonthsAgo.getDate());
  const minDate = `${minYyyy}-${minMm}-${minDd}`;

  // dateRange가 minDate보다 이전이면 자동 보정
  useEffect(() => {
    if (dateRange.start < minDate) {
      setDateRange(prev => ({ ...prev, start: minDate }));
    }
    if (dateRange.end < minDate) {
      setDateRange(prev => ({ ...prev, end: minDate }));
    }
  }, [dateRange.start, dateRange.end, minDate]);

  // dateRange가 바뀌면 pendingDateRange도 동기화
  useEffect(() => {
    setPendingDateRange(dateRange);
  }, [dateRange]);

  // 현재 화면에 해당하는 콘텐츠 탭 인덱스 (출고조회는 별도 화면)
  const getContentTabIndex = () => {
    const statsIndex = screenConfig.findIndex((s) => s.id === 'stats');
    const reportIndex = screenConfig.findIndex((s) => s.id === 'report');

    if (currentScreenIndex === 1) return 0;
    if (currentScreenIndex === 2) return 1;
    if (showStatsTab && currentScreenIndex === statsIndex) return 2;
    if (currentScreenIndex === reportIndex) return showStatsTab ? 3 : 2;
    return 0;
  };

  const contentTabIndex = getContentTabIndex();

  // 배차 데이터 fetch 함수
  const fetchData = async () => {
    // currentCompany가 설정되지 않았으면 데이터를 가져오지 않음
    if (!currentCompany || currentCompany.trim() === '') {
      console.log('[CustomerPage] currentCompany가 설정되지 않음, 데이터 fetch 건너뜀');
      setRows([]);
      setOrderCount(0);
      setUnassignedCount(0);
      return;
    }

    const dateList = getDateList(dateRange.start, dateRange.end);
    
    if (dateList.length === 0) {
      return;
    }

    console.log('[CustomerPage] fetchData 실행:', {
      dateList,
      currentCompany,
      isMobile,
      viewMode: isMobile ? 'card' : 'table'
    });

    setLoading(true);
    setHasQueried(true);
    // 이전 데이터 즉시 클리어
    setRows([]);
    setOrderCount(0);
    setUnassignedCount(0);
    
    try {
      const allData: any[] = [];
      
      for (const date of dateList) {
        const response = await fetch(`/api/dispatch-reports/${date}/rows?company=${encodeURIComponent(currentCompany)}`);
        const data: any[] = await response.json();
        
        if (data && Array.isArray(data)) {
          // 날짜 정보 추가
          const dataWithDate = data.map((row: any) => ({
            ...row,
            date: date
          }));
          
          allData.push(...dataWithDate);
        }
      }
      
      // 품명 → 운송구간(포함 일치) 순 필터 (관리자 부서 설정)
      const byItem = applyItemNameFilter(allData, itemNameFilterType, itemNameList || []);
      const filteredData = applyRouteFilter(byItem, routeFilterType || 'all', routeList || []);
      console.log('[CustomerPage] 데이터 로드 완료:', {
        totalRows: allData.length,
        filteredRows: filteredData.length,
        isMobile,
        viewMode: isMobile ? 'card' : 'table'
      });
      setRows(filteredData);
      
      // 오더 수와 미배차 수 계산 (필터 적용된 데이터 기준)
      const totalOrders = filteredData.filter(row => row.route && row.route.trim() !== '').length;
      const unassignedOrders = filteredData.filter(row => 
        row.route && row.route.trim() !== '' && 
        (!row.vehicleNumber || row.vehicleNumber.trim() === '')
      ).length;
      setOrderCount(totalOrders);
      setUnassignedCount(unassignedOrders);
      
    } catch (error) {
      console.error('데이터 fetch 실패:', error);
      setRows([]);
      setOrderCount(0);
      setUnassignedCount(0);
    } finally {
      setLoading(false);
    }
  };

  // 정산 엑셀 다운로드 함수
  const handleSettlementExcelDownload = () => {
    if (!settlementExcelRows) return;
    
    const ws = XLSX.utils.aoa_to_sheet([
      settlementExcelRows.headers,
      ...settlementExcelRows.rows
    ]);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '정산내역');
    const fileName = `${customerInfo?.name || '화주'}_정산내역_${settlementDateRange.start}_${settlementDateRange.end}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // 래퍼 함수 생성
  const handleSettlementExcelRowsChange = (rows: { headers: string[]; rows: any[][] }) => {
    setSettlementExcelRows(rows);
  };

  // customerInfo가 세팅될 때마다(최초 진입 포함) currentCompany 설정 및 fetchData 실행
  React.useEffect(() => {
    if (customerInfo) {
      // 화주별 필터링을 위한 회사명 설정
      let companyName = '';
      if (customerInfo.name === '오뚜기라면') {
        companyName = '오뚜기농협물류';
      } else if (customerInfo.name === '오뚜기물류서비스(주)') {
        companyName = '오뚜기물류서비스(주)';
      } else if (customerInfo.name === '돌코리아') {
        companyName = '돌코리아';
      } else if (customerInfo.name === '츄고쿠삼화페인트') {
        companyName = 'csp';
      } else if (customerInfo.name === '(주)엘앤비') {
        companyName = '(주)엘앤비';
      } else if (customerInfo.name === '대륭기업(주)') {
        companyName = '대륭기업(주)';
      } else if (customerInfo.name === '용신물류') {
        companyName = '용신물류';
      } else if (customerInfo.name === '성양로지스틱스') {
        companyName = '성양로지스틱스';
      } else if (customerInfo.name === '푸르미영농조합') {
        companyName = '푸르미영농조합';
      } else if (customerInfo.name === '케이디케미칼') {
        companyName = '케이디케미칼';
      } else if (customerInfo.name === '동양제관') {
        companyName = '동양제관';
      } else if (customerInfo.name === '(주)낙토') {
        companyName = '(주)낙토';
      } else if (customerInfo.name === '(주)부광정공') {
        companyName = '(주)부광정공';
      } else if (customerInfo.name === '하나로농협물류') {
        companyName = '하나로농협물류';
      }
      
      console.log('[CustomerPage] customerInfo 설정:', {
        customerInfo: customerInfo.name,
        companyName,
        isMobile,
        viewMode: isMobile ? 'card' : 'table'
      });
      
      setCurrentCompany(companyName);
    }
  }, [customerInfo, isMobile]);

  // currentCompany·기간·필터 설정이 바뀌면 재조회 (필터는 클라이언트에서도 반영)
  React.useEffect(() => {
    if (currentCompany && currentCompany.trim() !== '') {
      fetchData();
    }
  }, [
    currentCompany,
    dateRange.start,
    dateRange.end,
    itemNameFilterType,
    itemNameList,
    routeFilterType,
    routeList,
  ]);

  const isOttogi = customerId === 'otoki_ramen' || currentCompany === '오뚜기농협물류' || customerInfo?.name === '오뚜기라면';


  // tabItems 선언 (동적 구성)
  const tabItems = [
    { label: '배차', content: (
      <Box
        ref={dispatchScrollContainerRef}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: viewMode === 'table' ? 'hidden' : 'auto'
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: viewMode === 'table' ? 'hidden' : 'auto', bgcolor: '#fff', position: 'relative', touchAction: 'pan-y' }}>
          <CustomerDispatchReportViewer 
            rows={rows}
            loading={loading}
            isMobile={isMobile}
            fetchData={fetchData}
            dateRange={dateRange}
            currentCompany={currentCompany}
            customerInfo={customerInfo ?? undefined}
            customerId={customerId}
            viewType={viewType}
            hasQueried={hasQueried}
          />
        </Box>
      </Box>
    ) },
    { label: '정산', content: (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: '#fff', position: 'relative', touchAction: 'pan-y' }}>
          <CustomerSettlementViewer
            customerInfo={customerInfo}
            isMobile={isMobile}
            dateRange={settlementDateRange}
            apiCompanyName={currentCompany}
            onTotalFeeChange={setSettlementTotalFee}
            onExcelRowsChange={handleSettlementExcelRowsChange}
            hasQueried={settlementHasQueried}
            itemNameFilterType={itemNameFilterType}
            itemNameList={itemNameList}
            routeFilterType={routeFilterType}
            routeList={routeList}
          />
        </Box>
      </Box>
    ) },
  ];
  
  // 운송료 탭 조건부 추가
  if (showStatsTab) {
    tabItems.push({ label: '운송료', content: <CustomerStatsPage showStatsTab={showStatsTab} /> });
  }
  
  // 보고서 탭 추가
  tabItems.push({
    label: '보고서',
    content: <CustomerReportPage showStatsInReport={showStatsInReport} reportCompanyName={currentCompany} />,
  });

  // 현재 탭 인덱스가 유효한지 확인
  const validTabIndex = contentTabIndex >= 0 && contentTabIndex < tabItems.length ? contentTabIndex : 0;



  // JSX 반환
  return (
    <Box sx={{
      height: isMobile && validTabIndex === 0 ? '100%' : '100vh',
      minHeight: isMobile && validTabIndex === 0 ? '100%' : '100vh',
      bgcolor: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: 1
    }}>
      {/* 로딩 오버레이 제거 - 각 컴포넌트의 자체 로딩 스피너만 사용 */}
      {/* 상단 고정 영역: 화주 로고 + 탭 */}
      {customerInfo && !hideTopTabs && (
        <CustomerTopNav
          customerInfo={customerInfo}
          screenConfig={screenConfig}
          currentScreenIndex={currentScreenIndex}
          onNavigateHome={() => onNavigateToMain?.()}
          onNavigateScreen={(idx) => {
            window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: idx }));
          }}
        />
      )}

      {/* 고정 영역: 날짜 선택, 카운트, 버튼들 */}
      {validTabIndex === 0 && (
        <Box sx={{
          flexShrink: 0,
          bgcolor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          pb: 1,
          mb: 0,
          zIndex: 5,
          borderBottom: '1px solid #e3e8ef'
        }}>
          {/* 날짜 선택 + 조회 버튼 (모바일/PC 분기) */}
          {isMobile ? (
            <Box sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'nowrap',
              justifyContent: 'center',
              flexDirection: 'row',
              width: '100%',
              maxWidth: 400
            }}>
                                <TextField
                    type="date"
                    value={pendingDateRange.start}
                    onChange={(e) => setPendingDateRange(prev => ({ ...prev, start: e.target.value }))}
                    size="small"
                    inputProps={{ min: minDate }}
                    sx={{
                      width: 135,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: 13,
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e0e0e0 !important',
                        '&:hover': {
                          borderColor: '#1976d2 !important'
                        },
                        '&.Mui-focused': {
                          borderColor: '#1976d2 !important',
                          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2) !important'
                        }
                      }
                    }}
                  />
                  <Typography sx={{ color: '#666', fontSize: 13 }}>~</Typography>
                  <TextField
                    type="date"
                    value={pendingDateRange.end}
                    onChange={(e) => setPendingDateRange(prev => ({ ...prev, end: e.target.value }))}
                    size="small"
                    inputProps={{ min: minDate }}
                    sx={{
                      width: 135,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: 13,
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e0e0e0 !important',
                        '&:hover': {
                          borderColor: '#1976d2 !important'
                        },
                        '&.Mui-focused': {
                          borderColor: '#1976d2 !important',
                          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2) !important'
                        }
                      }
                    }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setDateRange(pendingDateRange);
                }}
                sx={{ height: 36, fontWeight: 700, minWidth: 48, px: 1.5, fontSize: 13, ml: 1 }}
              >
                조회
              </Button>
            </Box>
          ) : (
            <Box sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <TextField
                type="date"
                value={pendingDateRange.start}
                onChange={(e) => setPendingDateRange(prev => ({ ...prev, start: e.target.value }))}
                size="small"
                inputProps={{ min: minDate }}
                sx={{ 
                  width: { xs: 140, sm: 160 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: { xs: 14, sm: 16 },
                    backgroundColor: '#ffffff !important',
                    border: '1px solid #e0e0e0 !important',
                    '&:hover': {
                      borderColor: '#1976d2 !important'
                    },
                    '&.Mui-focused': {
                      borderColor: '#1976d2 !important',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2) !important'
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: '#333333 !important',
                    '&::placeholder': {
                      color: '#999999 !important'
                    }
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#666666 !important'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0 !important'
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2 !important'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2 !important'
                  }
                }}
              />
              <Typography sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>~</Typography>
              <TextField
                type="date"
                value={pendingDateRange.end}
                onChange={(e) => setPendingDateRange(prev => ({ ...prev, end: e.target.value }))}
                size="small"
                inputProps={{ min: minDate }}
                sx={{ 
                  width: { xs: 140, sm: 160 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: { xs: 14, sm: 16 },
                    backgroundColor: '#ffffff !important',
                    border: '1px solid #e0e0e0 !important',
                    '&:hover': {
                      borderColor: '#1976d2 !important'
                    },
                    '&.Mui-focused': {
                      borderColor: '#1976d2 !important',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2) !important'
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: '#333333 !important',
                    '&::placeholder': {
                      color: '#999999 !important'
                    }
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#666666 !important'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0 !important'
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2 !important'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2 !important'
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setDateRange(pendingDateRange);
                }}
                sx={{ height: 40, fontWeight: 700 }}
              >
                조회
              </Button>
            </Box>
          )}

          {/* 오더/미배차 카운트 */}
          {isMobile ? (
            // 모바일: 배차일보 스타일처럼 슬림하게
            <Box sx={{ 
              mb: 2, 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              padding: '8px 12px',
              gap: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              justifyContent: 'center'
            }}>
                             <span style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, whiteSpace: 'nowrap' }}>전체오더</span>
              <span style={{ 
                fontWeight: 700, 
                color: '#1976d2', 
                fontSize: 14,
                padding: '2px 8px',
                background: '#f0f8ff',
                borderRadius: 4,
                minWidth: 20,
                textAlign: 'center'
              }}>
                {orderCount}
              </span>
              <span style={{ fontWeight: 600, color: '#d32f2f', fontSize: 13, whiteSpace: 'nowrap' }}>미배차</span>
              <span style={{ 
                fontWeight: 700, 
                color: '#d32f2f', 
                fontSize: 14,
                padding: '2px 8px',
                background: '#fff5f5',
                borderRadius: 4,
                minWidth: 20,
                textAlign: 'center'
              }}>
                {unassignedCount}
              </span>
            </Box>
          ) : (
            // PC: 기존 스타일 유지
            <Box sx={{ 
              mb: 2, 
              display: 'flex', 
              gap: 3,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <Box sx={{ 
                textAlign: 'center',
                p: 1.5,
                bgcolor: '#ffffff',
                borderRadius: 2,
                minWidth: 80,
                border: '1px solid #e9ecef',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 20 } }}>
                  {orderCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: 12, sm: 14 } }}>
                  전체 오더
                </Typography>
              </Box>
              <Box sx={{ 
                textAlign: 'center',
                p: 1.5,
                bgcolor: '#ffffff',
                borderRadius: 2,
                minWidth: 80,
                border: '1px solid #e9ecef',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#f44336', fontSize: { xs: 18, sm: 20 } }}>
                  {unassignedCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: 12, sm: 14 } }}>
                  미배차
                </Typography>
              </Box>
            </Box>
          )}

          {/* 오뚜기라면만 라디오 버튼 노출 */}
          {isOttogi && (
            <OttogiViewTypeSelector viewType={viewType} setViewType={setViewType} />
          )}


        </Box>
      )}

      {/* 정산 탭에서만 표시되는 영역 */}
      {validTabIndex === 1 && (
        <Box sx={{
          flexShrink: 0,
          bgcolor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          pb: 1,
          mb: 0,
          zIndex: 5
        }}>
          {/* 안내 메시지 */}
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2, 
              color: '#444444',
              fontSize: isMobile ? 14 : 16,
              lineHeight: 1.6,
              textAlign: 'center'
            }}
          >
            정산 자료 미리보기 화면입니다<br />
            정산 마감 시 엑셀/PDF 파일로 정산 마감 자료를 송부해드리겠습니다.
          </Typography>

          {/* 날짜 선택 + 조회 버튼 (모바일/PC 분기) */}
          {isMobile ? (
            <Box sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'nowrap',
              justifyContent: 'center',
              flexDirection: 'row',
              width: '100%',
              maxWidth: 400
            }}>
              <TextField
                type="date"
                value={pendingDateRange.start}
                onChange={(e) => setPendingDateRange(prev => ({ ...prev, start: e.target.value }))}
                size="small"
                inputProps={{ min: minDate }}
                sx={{
                  width: 135,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: 13
                  }
                }}
              />
              <Typography sx={{ color: '#666', fontSize: 13 }}>~</Typography>
              <TextField
                type="date"
                value={pendingDateRange.end}
                onChange={(e) => setPendingDateRange(prev => ({ ...prev, end: e.target.value }))}
                size="small"
                inputProps={{ min: minDate }}
                sx={{
                  width: 135,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: 13
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (validTabIndex === 1) {
                    setSettlementDateRange(pendingDateRange);
                    setSettlementHasQueried(true);
                    // 정산 화면은 dateRange 변경으로 자동 fetch
                  } else {
                    setDateRange(pendingDateRange);
                  }
                }}
                sx={{ height: 36, fontWeight: 700, minWidth: 48, px: 1.5, fontSize: 13, ml: 1 }}
              >
                조회
              </Button>
            </Box>
          ) : (
            <Box sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center',
              flexDirection: 'row',
              width: '100%',
              maxWidth: 600
            }}>
              <TextField
                type="date"
                value={pendingDateRange.start}
                onChange={(e) => setPendingDateRange(prev => ({ ...prev, start: e.target.value }))}
                size="small"
                inputProps={{ min: minDate }}
                sx={{
                  width: 140,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: 14
                  }
                }}
              />
              <Typography sx={{ color: '#666', fontSize: 14 }}>~</Typography>
              <TextField
                type="date"
                value={pendingDateRange.end}
                onChange={(e) => setPendingDateRange(prev => ({ ...prev, end: e.target.value }))}
                size="small"
                inputProps={{ min: minDate }}
                sx={{
                  width: 140,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: 14
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (validTabIndex === 1) {
                    setSettlementDateRange(pendingDateRange);
                    setSettlementHasQueried(true);
                    // 정산 화면은 dateRange 변경으로 자동 fetch
                  } else {
                    setDateRange(pendingDateRange);
                  }
                }}
                sx={{ height: 40, fontWeight: 700, minWidth: 60, px: 2, fontSize: 15 }}
              >
                조회
              </Button>
            </Box>
          )}

          {/* 운송료 표시 (오뚜기 제외) */}
          {customerInfo?.name !== '오뚜기라면' && (
            <Box sx={{ 
              mb: 0, 
              p: 2, 
              bgcolor: customerInfo?.lightColor || '#e3f2fd',
              borderRadius: 2,
              border: `1px solid ${customerInfo?.color || '#1976d2'}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              width: '100%',
              maxWidth: 600
            }}>
              <Typography 
                variant="h6" 
                fontWeight={700}
                sx={{ 
                  color: customerInfo?.color || '#1976d2',
                  fontSize: isMobile ? 16 : 18,
                  flex: 1,
                  textAlign: 'center'
                }}
              >
                운송료: {settlementTotalFee > 0 ? settlementTotalFee.toLocaleString() : ''}원
              </Typography>
              {/* PC에서만 엑셀 다운로드 버튼 표시 */}
              {!isMobile && (
                <Button
                  variant="contained"
                  startIcon={<GetAppIcon />}
                  onClick={handleSettlementExcelDownload}
                  disabled={settlementTotalFee === 0}
                  sx={{
                    bgcolor: customerInfo?.color || '#1976d2',
                    color: '#fff',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: `${customerInfo?.color || '#1976d2'}dd`
                    },
                    '&:disabled': {
                      bgcolor: '#ccc',
                      color: '#999'
                    }
                  }}
                >
                  엑셀
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* 탭별 화면 */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: validTabIndex === 1 ? 'hidden' : 'auto', // 정산 탭만 hidden, 나머지는 auto
          bgcolor: '#ffffff',
          position: 'relative',
          touchAction: 'pan-y'
        }}
      >
        {tabItems[validTabIndex]?.content}
      </Box>


    </Box>
  );
};

const CustomerPage = () => {
  useEffect(() => {
  }, []);
  return <Outlet />;
};

export default CustomerPage; 